import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api/client";

export interface StaffingPoint {
  date: string;
  rostered: number;
  required: number;
}

/** The same fortnight, for one branch. */
export interface BranchStaffing {
  branch_id: string;
  branch_name: string;
  points: StaffingPoint[];
}

/** What is waiting behind each rail item, keyed by the item's countKey. */
export type NavCounts = Record<string, number>;

export interface OvertimePoint {
  week_start: string;
  hours: number;
}

export interface ExpiringDocument {
  user_id: string;
  employee_name: string;
  employee_number: string | null;
  document: string;
  expires_on: string;
  days_left: number;
}

export interface UnderstaffedDay {
  date: string;
  branches: {
    branch_id: string;
    branch_name: string;
    headcount: number;
    min_headcount: number;
  }[];
}

export interface PendingTask {
  key: string;
  label: string;
  count: number;
  link: string;
  urgency: "urgent" | "normal" | "low";
}

export interface ProbationEnding {
  user_id: string;
  employee_name: string;
  employee_number: string | null;
  position_title: string | null;
  date_of_joining: string | null;
  probation_ends: string;
  days_left: number;
}

export interface Dashboard {
  tasks: PendingTask[];
  probation_ending: ProbationEnding[];
  pending_accounts: number;
  pending_change_requests: number;
  pending_leave_requests: number;
  documents_awaiting_signature: number;
  signatures_outstanding: number;
  unmatched_badges: number;
  overtime_needing_review: number;
  /** Contractual reviews inside their notice period and not yet decided. */
  salary_reviews_due: number;
  expiring_documents: ExpiringDocument[];
  understaffed_days: UnderstaffedDay[];
  active_employees: number;
  /** Active staff on approved leave today. */
  on_leave_today: number;
  /** Active staff at work today: on the rota, or, with no rota, on a working day. */
  on_duty_today: number;
  /** Active staff on their day off today. */
  rest_day_today?: number;
  /** Active staff neither at work, on leave, nor on a day off. */
  not_scheduled_today?: number;
  /** Effective overtime across everyone this calendar month, in hours. */
  overtime_hours_month: number;
  /** Joiners less leavers over the last 30 days. Can be negative. */
  headcount_change_30d: number;
  staffing_trend: StaffingPoint[];
  overtime_trend: OvertimePoint[];
  staffing_by_branch: BranchStaffing[];
}

/** One person on today's board. */
export interface TodayPerson {
  user_id: string;
  name: string;
  employee_number: string | null;
  position: string | null;
  photo_file_id: string | null;
  /** The shift's branch when on duty, else their own. */
  branch_id: string | null;
  branch_name: string | null;
}

export interface TodayOnDuty extends TodayPerson {
  /** Null when no rota covers them and their record says they work today. */
  shift_name: string | null;
  start_time: string | null;
  end_time: string | null;
  ends_next_day: boolean;
  covering_from: string | null;
  on_rest_day: boolean;
}

export interface TodayOnLeave extends TodayPerson {
  leave_type: string;
  until: string | null;
}

export interface TodayNotWorking extends TodayPerson {
  reason: string;
}

/** Everybody active, in the one place they are today. */
export interface TodayBoard {
  date: string;
  rota_drawn: boolean;
  on_duty: TodayOnDuty[];
  on_leave: TodayOnLeave[];
  rest_day: TodayNotWorking[];
  not_scheduled: TodayNotWorking[];
}

export const dashboardKeys = {
  all: ["dashboard"] as const,
  counts: (who: "admin" | "me") => [...dashboardKeys.all, "counts", who] as const,
  today: () => [...dashboardKeys.all, "today"] as const,
};

/** Who is on duty today, by name: the list behind the "On duty today" figure. */
export function useToday() {
  return useQuery({
    queryKey: dashboardKeys.today(),
    queryFn: () => api.get<TodayBoard>("/dashboard/today"),
    refetchInterval: 60_000,
  });
}

/**
 * The numbers beside the rail items.
 *
 * One request a minute for the whole rail, rather than one per item, and a
 * separate one for HR and for an employee because they are counting
 * different things. Stale for the minute between: a count is a nudge, not
 * a ledger.
 */
export function useNavCounts(who: "admin" | "me" | null) {
  return useQuery({
    queryKey: dashboardKeys.counts(who ?? "me"),
    queryFn: () => api.get<NavCounts>(who === "admin" ? "/dashboard/counts" : "/me/counts"),
    enabled: who !== null,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function useDashboard() {
  return useQuery({
    queryKey: dashboardKeys.all,
    queryFn: () => api.get<Dashboard>("/dashboard"),
    // The point of this page is what is waiting now.
    refetchInterval: 60_000,
  });
}
